import React from "react";
import {Card, Tag} from "antd";
import {DollarOutlined} from "@ant-design/icons";
import moment from "moment";
import {renderAmount} from "../../../helpers/renderAmount";

/**
 * Wiederverwendbare Karte fuer kombinierte Raten-Aenderungen
 * (DELETE + ADD im selben Speichervorgang).
 *
 * Extrahiert aus `CourseHistoryContent.tsx` (Z. 2500-2623). Wird heute nur
 * von der Course-Historie genutzt - die Komponente ist aber generisch genug,
 * um auch fuer Booking-Historie verwendet zu werden, sobald Stufe 2 des
 * Booking-History-Plans (eigene Karten fuer ScheduledPaymentCreated /
 * ScheduledPaymentDeleted) angegangen wird.
 */

/**
 * Eine extrahierte Rate (entweder aus alten oder neuen Werten).
 */
export type ExtractedRate = {
    amount: number | undefined;
    dueDate: string | undefined;
    type: string | undefined;
};

/**
 * Minimal-Form, die diese Karte aus History-Items braucht.
 * Bewusst lose typisiert (wir lesen aus JSONB-Snapshots), damit die
 * Komponente sowohl mit `CourseHistoryItem` als auch mit
 * `BookingHistoryItem` funktioniert.
 */
export type RateExtractionItem = {
    oldValues?: unknown;
    newValues?: unknown;
};

/**
 * Extrahiert Rate-Informationen aus History-Items (Bulk- und Einzel-Format).
 * Akzeptiert sowohl `oldValues` (DELETE) als auch `newValues` (ADD).
 */
export const extractRatesFromItems = (items: ReadonlyArray<RateExtractionItem>): ExtractedRate[] => {
    const rates: ExtractedRate[] = [];

    items.forEach((item) => {
        // Fuer DELETE: oldValues enthaelt die Daten
        // Fuer ADD: newValues enthaelt die Daten
        const vals = (item.oldValues ?? item.newValues) as Record<string, unknown> | undefined;

        if (!vals) return;

        // Pruefe auf Bulk-Format mit rates-Array
        const ratesArray = vals.rates as Array<Record<string, unknown>> | undefined;

        if (ratesArray && Array.isArray(ratesArray) && ratesArray.length > 0) {
            // Bulk-Format: Extrahiere jede Rate
            ratesArray.forEach((rate) => {
                rates.push({
                    amount: rate.installmentAmount as number | undefined,
                    dueDate: rate.installmentDue as string | undefined,
                    type: rate.installmentType as string | undefined,
                });
            });
        } else if (vals.installmentAmount !== undefined || vals.installmentDue !== undefined) {
            // Einzel-Format
            rates.push({
                amount: vals.installmentAmount as number | undefined,
                dueDate: vals.installmentDue as string | undefined,
                type: vals.installmentType as string | undefined,
            });
        }
    });

    // Sortiere nach Faelligkeitsdatum
    rates.sort((a, b) => {
        if (!a.dueDate && !b.dueDate) return 0;
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;

        return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
    });

    return rates;
};

type RatesChangedCardProps = {
    deletedItems: ReadonlyArray<RateExtractionItem>;
    addedItems: ReadonlyArray<RateExtractionItem>;
    currencySettings: {short: string; symbol: string};
};

/**
 * Komponente fuer kombinierte Raten-Aenderungen (wenn DELETE + ADD
 * gleichzeitig passieren). Zeigt: Anzahl Raten, Faelligkeiten, Betraege
 * als Vorher/Nachher-Vergleich.
 */
const RatesChangedCard: React.FC<RatesChangedCardProps> = ({deletedItems, addedItems, currencySettings}) => {
    const oldRates = extractRatesFromItems(deletedItems);
    const newRates = extractRatesFromItems(addedItems);

    const oldTotal = oldRates.reduce((sum, r) => sum + (r.amount ?? 0), 0);
    const newTotal = newRates.reduce((sum, r) => sum + (r.amount ?? 0), 0);

    const formatAmount = (amount: number | undefined) => {
        if (amount === undefined) return "-";

        return renderAmount({amount, currency: currencySettings.short as "EUR" | "CHF" | "USD" | "GBP"});
    };

    const formatDate = (dateStr: string | undefined) => {
        if (!dateStr) return "-";

        return moment(dateStr).format("DD.MM.YYYY");
    };

    // Bestimme die maximale Anzahl fuer die Tabellenzeilen
    const maxRates = Math.max(oldRates.length, newRates.length);

    return (
        <Card
            size="small"
            title={
                <div style={{display: "flex", alignItems: "center", gap: 8}}>
                    <Tag icon={<DollarOutlined />} color="gold">
                        Raten geändert
                    </Tag>
                    <span style={{fontWeight: 400, color: "#666"}}>
                        ({oldRates.length} → {newRates.length} Raten)
                    </span>
                </div>
            }
            style={{marginBottom: 16}}
        >
            {/* Zusammenfassung */}
            <div style={{marginBottom: 12, padding: "8px 12px", backgroundColor: "#fafafa", borderRadius: 4}}>
                <div style={{display: "flex", gap: 24, fontSize: "0.9em"}}>
                    <div>
                        <span style={{color: "#666"}}>Anzahl: </span>
                        <span style={{color: "#999"}}>{oldRates.length}</span>
                        <span style={{color: "#666", margin: "0 4px"}}>→</span>
                        <Tag color={oldRates.length !== newRates.length ? "blue" : "default"} style={{margin: 0}}>
                            {newRates.length}
                        </Tag>
                    </div>
                    <div>
                        <span style={{color: "#666"}}>Summe: </span>
                        <span style={{color: "#999"}}>{formatAmount(oldTotal)}</span>
                        <span style={{color: "#666", margin: "0 4px"}}>→</span>
                        <Tag color={Math.abs(oldTotal - newTotal) > 0.01 ? "green" : "default"} style={{margin: 0}}>
                            {formatAmount(newTotal)}
                        </Tag>
                    </div>
                </div>
            </div>

            {/* Detaillierte Raten-Gegenueberstellung */}
            <div style={{fontSize: "0.85em"}}>
                {/* Header */}
                <div style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: 16,
                    marginBottom: 8,
                    paddingBottom: 8,
                    borderBottom: "1px solid #f0f0f0",
                }}>
                    <div style={{fontWeight: 500, color: "#666"}}>Vorher</div>
                    <div style={{fontWeight: 500, color: "#666"}}>Nachher</div>
                </div>

                {/* Raten-Zeilen */}
                {Array.from({length: maxRates}).map((_, idx) => {
                    const oldRate = oldRates[idx];
                    const newRate = newRates[idx];

                    return (
                        <div
                            key={idx}
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 16,
                                padding: "6px 0",
                                borderBottom: idx < maxRates - 1 ? "1px dashed #f0f0f0" : "none",
                            }}
                        >
                            {/* Alte Rate */}
                            <div style={{color: oldRate ? "#999" : "#ccc"}}>
                                {oldRate ? (
                                    <>
                                        <span>{formatDate(oldRate.dueDate)}</span>
                                        <span style={{marginLeft: 8}}>{formatAmount(oldRate.amount)}</span>
                                    </>
                                ) : (
                                    <span style={{fontStyle: "italic"}}>-</span>
                                )}
                            </div>

                            {/* Neue Rate */}
                            <div>
                                {newRate ? (
                                    <>
                                        <span style={{color: "#333"}}>{formatDate(newRate.dueDate)}</span>
                                        <Tag
                                            color={oldRate && Math.abs((oldRate.amount ?? 0) - (newRate.amount ?? 0)) > 0.01 ? "green" : "default"}
                                            style={{marginLeft: 8}}
                                        >
                                            {formatAmount(newRate.amount)}
                                        </Tag>
                                    </>
                                ) : (
                                    <span style={{fontStyle: "italic", color: "#ccc"}}>-</span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </Card>
    );
};

export default RatesChangedCard;
